<template>
  <div ref="root" class="bg-white rounded-lg border border-gray-200 overflow-hidden flex flex-col" style="height: calc(100vh - 220px); min-height: 720px;">
    <ComponentHeader
      title="Workflows"
      subtitle="Compose workflows from data attributes on the canvas. A workflow reads live until you give it a cadence, approve it and materialize it."
    >
      <!-- New Workflow lives in the composer (Clear button) + the empty-rail
           state, so the header only needs Refresh. -->
      <button
        class="btn-secondary"
        :disabled="isLoading"
        @click="refreshWorkflows"
      >
        <font-awesome-icon :icon="['fas', 'arrows-rotate']" :spin="isLoading" />
        Refresh
      </button>
      <!-- Connect Workflow Builder to an external warehouse (Snowflake / Azure /
           JDBC-ODBC) so workflows can read+write the customer's own data. -->
      <button class="btn-accent ml-2" @click="connectionModalVisible = true">
        <font-awesome-icon :icon="['fas', 'database']" />
        Add Data Connection
      </button>
    </ComponentHeader>

    <!-- Split view: workflow list rail (left) + live canvas composer (right) -->
    <div class="flex flex-1 min-h-0">
      <WorkflowFieldsPanel
        :sheet-active="!!activeSheet"
        @add-source="onAddSource"
        @field-click="onFieldClick"
        @field-action="onFieldAction"
        @edit-calc="(name) => $refs.canvas.editCalcByName(name)"
        @analytics="(kind) => $refs.sheet && $refs.sheet.applyAnalytic(kind)"
      />

      <!-- ── Right: composer bar + canvas ─────────────────────────────────── -->
      <section class="flex-1 min-w-0 flex flex-col">
        <!-- Composer bar: name + cadence + lifecycle actions for the workflow
             currently loaded on the canvas. -->
        <div class="flex flex-wrap items-end gap-x-3 gap-y-2 px-4 py-2.5 border-b border-gray-200 bg-white">
          <div ref="picker" class="relative w-48 shrink-0">
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Workflow</label>
            <button
              type="button"
              class="w-full flex items-center gap-2 border border-gray-300 rounded-md px-3 py-1.5 text-sm text-left bg-white hover:border-gray-400 transition"
              :aria-expanded="pickerOpen"
              @click="pickerOpen = !pickerOpen"
            >
              <span v-if="activeWorkflow" :class="statusDotClass(activeWorkflow.status)"></span>
              <span class="flex-1 truncate" :class="activeWorkflow ? 'text-gray-800' : 'text-gray-500'">{{ activeWorkflow ? activeWorkflow.name : 'New workflow' }}</span>
              <span class="text-xs text-gray-400 tabular-nums">{{ workflows.length }}</span>
              <font-awesome-icon :icon="['fas', 'chevron-down']" class="text-gray-400 text-xs" />
            </button>
            <div v-if="pickerOpen" class="absolute z-30 left-0 top-full mt-1 w-80 bg-white border border-gray-200 rounded-lg shadow-lg flex flex-col">
              <button class="flex items-center gap-2 px-3 py-2 text-sm text-blue-dark border-b border-gray-200 hover:bg-gray-50" @click="pickNew">
                <font-awesome-icon :icon="['fas', 'plus']" /> New workflow
              </button>
          <!-- Search -->
          <div class="px-3 py-2 border-b border-gray-200">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by name…"
              class="w-full border border-gray-300 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-dark/60 transition"
            />
          </div>

          <!-- Loading skeleton -->
          <div v-if="isLoading && !workflows.length" class="p-3 space-y-2">
            <div v-for="n in 5" :key="n" class="h-8 rounded skeleton-shimmer"></div>
          </div>

          <!-- Grouped list -->
          <div v-else class="overflow-y-auto max-h-80 py-1">
            <template v-for="group in groupedWorkflows" :key="group.status">
              <div v-if="group.items.length" class="mb-1">
                <div class="flex items-center gap-2 px-3 py-1.5">
                  <span :class="statusDotClass(group.status)"></span>
                  <span class="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{{ statusLabel(group.status) }}</span>
                  <span class="text-[11px] text-gray-400 tabular-nums">{{ group.items.length }}</span>
                </div>
                <!-- Row is a div, not a button: the overflow DropdownMenu renders
                     its own <button>, and nesting one button inside another is
                     invalid HTML (browsers reparent it, breaking both handlers).
                     The menu is a sibling of the clickable area so its clicks
                     never reach selectWorkflow. -->
                <div
                  v-for="wf in group.items"
                  :key="wf.id"
                  class="group w-full px-3 py-2 flex items-center gap-2 border-l-2 transition-colors"
                  :class="activeId === wf.id
                    ? 'bg-blue-50/70 border-blue-dark'
                    : 'border-transparent hover:bg-gray-100/70'"
                >
                  <div
                    class="text-left flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                    role="button"
                    tabindex="0"
                    @click="pick(wf)"
                    @keydown.enter.prevent="pick(wf)"
                    @keydown.space.prevent="pick(wf)"
                  >
                    <font-awesome-icon :icon="['fas', 'diagram-project']" class="text-primary-500/70 text-xs shrink-0" />
                    <span class="text-sm text-gray-800 truncate flex-1" :class="{ 'font-semibold': activeId === wf.id }">{{ wf.name }}</span>
                    <!-- health dot -->
                    <span
                      v-if="wf.last_run_status"
                      :class="healthDotClass(wf.last_run_status)"
                      :title="wf.last_run_error || wf.last_run_status"
                    ></span>
                  </div>
                  <!-- overflow -->
                  <DropdownMenu class="shrink-0">
                    <template #trigger>
                      <span class="opacity-0 group-hover:opacity-100 transition-opacity" title="More actions">
                        <font-awesome-icon :icon="['fas', 'ellipsis-vertical']" />
                      </span>
                    </template>
                    <DropdownMenuItem @click="pick(wf)">Edit</DropdownMenuItem>
                    <DropdownMenuItem v-if="wf.status === 'draft'" @click="submitForReview(wf)">Submit for review</DropdownMenuItem>
                    <DropdownMenuItem v-if="wf.status === 'pending_review'" @click="askApprove(wf)">Approve</DropdownMenuItem>
                    <DropdownMenuItem v-if="wf.status === 'pending_review'" @click="askReject(wf)">Reject</DropdownMenuItem>
                    <DropdownMenuItem v-if="wf.status === 'approved'" @click="materializeNow(wf)">Materialize now</DropdownMenuItem>
                    <DropdownMenuItem @click="confirmDelete(wf)">
                      <span class="text-red-dark">Delete</span>
                    </DropdownMenuItem>
                  </DropdownMenu>
                </div>
              </div>
            </template>

            <div v-if="!isLoading && !filteredWorkflows.length" class="px-4 py-10 text-center">
              <p class="text-sm text-gray-500">{{ searchQuery ? 'No matches.' : 'No workflows yet.' }}</p>
              <button v-if="!searchQuery" class="btn-accent-sm mt-3" @click="pickNew">
                <font-awesome-icon :icon="['fas', 'plus']" /> New Workflow
              </button>
            </div>
          </div>
            </div>
          </div>
          <div class="flex-1 min-w-[120px]">
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Name</label>
            <input
              v-model.trim="composer.name"
              type="text"
              placeholder="e.g. Readmission cohort"
              class="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-dark/60 transition"
            />
          </div>
          <div class="w-28 shrink-0">
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Cadence</label>
            <select v-model="composer.expected_cadence" class="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-dark/60 transition">
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="none">Not scheduled</option>
            </select>
          </div>

          <!-- Run order: workflows materialize low→high, so a workflow that reads
               another's output table must have a higher order than it. -->
          <div class="w-16 shrink-0">
            <label class="block text-[11px] font-medium text-gray-500 mb-1" title="Workflows run low to high. Set higher than any workflow whose output this one reads.">
              Run order
            </label>
            <input
              v-model.number="composer.post_process_order"
              type="number"
              min="0"
              step="1"
              class="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-dark/60 transition"
            />
          </div>

          <!-- status chip for the loaded workflow -->
          <div v-if="activeWorkflow" class="pb-1.5">
            <span :class="statusBadgeClass(activeWorkflow.status)">
              <span :class="statusDotClass(activeWorkflow.status)"></span>{{ statusLabel(activeWorkflow.status) }}
            </span>
          </div>

          <div class="flex items-center gap-2 ml-auto pb-0.5">
            <button class="btn-secondary-sm" :class="{ 'bg-gray-100': docsOpen }" :aria-pressed="docsOpen" title="Describe this workflow and its columns" @click="toggleDocs">Docs</button>
            <button v-if="activeId" class="btn-secondary-sm" @click="startNew">Clear</button>
            <button class="btn-primary-sm" :disabled="!canSave || saving" @click="saveWorkflow">
              <font-awesome-icon v-if="saving" :icon="['fas', 'arrows-rotate']" spin />
              {{ activeId ? 'Save changes' : 'Save' }}
            </button>
            <!-- Lifecycle actions match the loaded workflow's status -->
            <button
              v-if="activeWorkflow && activeWorkflow.status === 'draft'"
              class="btn-secondary-sm" :disabled="actingId === activeId"
              @click="submitForReview(activeWorkflow)"
            >Submit</button>
            <button
              v-if="activeWorkflow && activeWorkflow.status === 'pending_review'"
              class="btn-accent-sm" :disabled="actingId === activeId"
              @click="askApprove(activeWorkflow)"
            >Approve</button>
          </div>
        </div>
        <p v-if="composerError" class="px-4 pt-2 text-xs text-red-dark">{{ composerError }}</p>

        <div v-if="docsOpen" class="px-4 py-3 border-b border-gray-200 bg-gray-50/60 space-y-2">
          <div>
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Description</label>
            <textarea
              v-model="docs.description"
              rows="2"
              placeholder="What this datastream is and what one row means."
              class="sensitive w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-dark/60 transition"
              data-demo-blur
            />
          </div>
          <div v-if="docColumns.length">
            <label class="block text-[11px] font-medium text-gray-500 mb-1">Columns</label>
            <div class="max-h-40 overflow-y-auto space-y-1">
              <div v-for="c in docColumns" :key="c" class="flex items-center gap-2">
                <span class="w-48 shrink-0 truncate font-mono text-[11px] text-gray-700">{{ c }}</span>
                <input
                  :value="docs.column_docs[c] || ''"
                  placeholder="What this column holds"
                  class="sensitive flex-1 border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-dark/60"
                  data-demo-blur
                  @input="setColumnDoc(c, $event.target.value)"
                />
              </div>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <button v-if="activeId" class="btn-primary-sm" :disabled="savingDocs" @click="saveDocs">Save documentation</button>
            <span class="text-[11px] text-gray-500">{{ activeId ? 'Documentation never changes the workflow or its approval.' : 'Saved with the workflow.' }}</span>
          </div>
        </div>

        <div ref="stack" class="flex-1 min-h-0 flex flex-col">
          <div class="min-h-0 px-4 pt-3 pb-2" :style="{ height: `${stackPct}%` }">
            <WorkflowJoinCanvas
              ref="canvas"
              v-model="studioConfig"
              :workflow-meta="canvasWorkflowMeta"
              external-preview
              hide-source-rail
              class="wf-canvas-fill"
              @select="selection = $event"
              @refused="flashError"
            />
          </div>
          <div
            class="wf-hsplit"
            role="separator"
            aria-orientation="horizontal"
            title="Drag to resize · double-click to reset"
            @pointerdown="startStackResize"
            @dblclick="setCanvasPct(activeSheet ? SHEET_CANVAS_PCT : DEFAULT_CANVAS_PCT)"
          ></div>
          <div class="flex-1 min-h-0 flex flex-col">
            <div class="wf-btabs">
              <button class="wf-btab" :class="{ 'is-on': bottomTab === 'data' }" @click="bottomTab = 'data'">Data</button>
              <button
                v-for="sh in sheets"
                :key="sh.id"
                class="wf-btab"
                :class="{ 'is-on': bottomTab === sh.id }"
                @click="bottomTab = sh.id"
              >
                {{ sh.name || 'Sheet' }}
                <span class="wf-btab-x" title="Delete sheet" @click.stop="removeSheet(sh.id)">×</span>
              </button>
              <button class="wf-btab wf-btab-add" title="New sheet" @click="addSheet()">+</button>
              <span v-if="sheetsSaveState" class="wf-btab-state">{{ sheetsSaveState }}</span>
            </div>
            <WorkflowDataPane
              v-show="bottomTab === 'data'"
              ref="dataPane"
              class="flex-1 min-h-0"
              :studio-config="studioConfig"
              :selection="selection"
              :workflow-key="activeId"
              :export-workflow="sheetSource && sheetSource.workflow_id ? activeWorkflow : null"
            />
            <WorkflowSheet
              v-if="activeSheet"
              ref="sheet"
              :key="activeSheet.id"
              class="flex-1 min-h-0"
              :sheet="activeSheet"
              :source="sheetSource"
              :dashboard-workflow-id="dashboardWorkflowId"
              @update="updateSheet"
              @added-to-dashboard="onAddedToDashboard"
            />
          </div>
        </div>
      </section>

    </div>

    <!-- Confirm dialog (delete + approve/reject) -->
    <transition name="wf-fade">
    <div
      v-if="confirm.visible"
      class="fixed inset-0 bg-gray-600/50 flex items-center justify-center z-50"
      @click="closeConfirm"
    >
      <div class="wf-fade-panel bg-white rounded-lg p-6 max-w-sm w-full shadow-xl" @click.stop>
        <h3 class="text-lg font-medium text-gray-900">{{ confirm.title }}</h3>
        <p class="mt-2 text-sm text-gray-500">{{ confirm.body }}</p>
        <div class="mt-5 flex justify-end gap-2">
          <button class="btn-secondary" @click="closeConfirm">Cancel</button>
          <button :class="confirm.confirmClass" :disabled="confirm.busy" @click="confirm.onConfirm">
            {{ confirm.busy ? 'Working…' : confirm.confirmLabel }}
          </button>
        </div>
      </div>
    </div>
    </transition>

    <!-- Toasts (slide-in, auto-dismiss, icon) -->
    <transition name="wf-toast">
      <div v-if="errorMessage" class="fixed bottom-4 right-4 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg max-w-md z-50">
        <font-awesome-icon :icon="['fas', 'exclamation-circle']" class="text-red-dark mt-0.5" />
        <p class="text-sm text-red-800">{{ errorMessage }}</p>
      </div>
    </transition>
    <transition name="wf-toast">
      <div v-if="successMessage" class="fixed bottom-4 right-4 flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-4 shadow-lg max-w-md z-50">
        <font-awesome-icon :icon="['fas', 'check-circle']" class="text-green-dark mt-0.5" />
        <p class="text-sm text-green-800">{{ successMessage }}</p>
      </div>
    </transition>

    <!-- Add Data Connection: connect an external warehouse (Snowflake / Azure / JDBC-ODBC) -->
    <AddDataConnectionModal
      :visible="connectionModalVisible"
      @close="connectionModalVisible = false"
      @created="onConnectionCreated"
    />
  </div>
</template>

<script>
import axios from 'axios';
import { z } from 'zod';
import WorkflowJoinCanvas from './WorkflowJoinCanvas.vue';
import WorkflowDataPane from './WorkflowDataPane.vue';
import WorkflowFieldsPanel from './WorkflowFieldsPanel.vue';
import WorkflowSheet from './WorkflowSheet.vue';
import { workflowConfigKey } from '@/utils/workflowConfigKey';
import AddDataConnectionModal from './AddDataConnectionModal.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import DropdownMenu from '@/components/widgetcomponents/ui/DropdownMenu.vue';
import DropdownMenuItem from '@/components/widgetcomponents/ui/DropdownMenuItem.vue';
import { canvasBridge, canvasContext } from '@/services/canvasContext';
import { BrowserStore } from '@/utils/BrowserStore';

const API_BASE = '/api/etl_module/data-workflows/';
const stackStore = new BrowserStore('workflow-builder-stack-v1', z.object({
  canvas: z.number().nullable().default(null),
  sheetCanvas: z.number().nullable().default(null),
}));
const DEFAULT_CANVAS_PCT = 52;
const MIN_PCT = 25;
const SHEET_CANVAS_PCT = 38;
const MAX_PCT = 80;

// Status groups, in the order they appear in the rail.
const STATUS_ORDER = ['draft', 'pending_review', 'approved'];

export default {
  name: 'OverviewETLWorkflowBuilder',

  components: {
    WorkflowJoinCanvas,
    WorkflowDataPane,
    WorkflowFieldsPanel,
    WorkflowSheet,
    AddDataConnectionModal,
    ComponentHeader,
    DropdownMenu,
    DropdownMenuItem,
  },

  data() {
    return {
      workflows: [],
      searchQuery: '',
      isLoading: false,
      errorMessage: null,
      successMessage: null,

      connectionModalVisible: false,
      pickerOpen: false,
      selection: null,
      canvasPct: stackStore.load().canvas || DEFAULT_CANVAS_PCT,
      sheetCanvasPct: stackStore.load().sheetCanvas || SHEET_CANVAS_PCT,
      DEFAULT_CANVAS_PCT,
      SHEET_CANVAS_PCT,

      docsOpen: false,
      docs: { description: '', column_docs: {} },
      docColumns: [],
      savingDocs: false,

      visibilityObserver: null,
      sheets: [],
      bottomTab: 'data',
      sheetsTimer: null,
      sheetsSaveState: '',
      bridge: canvasBridge,

      // Composer: the workflow currently loaded on the canvas. activeId is null
      // for a fresh (unsaved) workflow.
      activeId: null,
      composer: { name: '', expected_cadence: 'weekly', post_process_order: 0 },
      studioConfig: { steps: [], primary_key_columns: [] },
      composerError: null,
      saving: false,

      // Id of the workflow a lifecycle action is currently running on.
      actingId: null,

      // Shared confirm dialog (delete / approve / reject).
      confirm: {
        visible: false, title: '', body: '', confirmLabel: '',
        confirmClass: 'btn-danger', busy: false, onConfirm: () => {},
      },
    };
  },

  computed: {
    filteredWorkflows() {
      let filtered = this.workflows;
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(w => w.name.toLowerCase().includes(query));
      }
      return filtered;
    },

    // Rail list bucketed by status (Draft / Pending Review / Approved).
    groupedWorkflows() {
      const byStatus = {};
      for (const s of STATUS_ORDER) byStatus[s] = [];
      for (const wf of this.filteredWorkflows) {
        (byStatus[wf.status] || (byStatus[wf.status] = [])).push(wf);
      }
      for (const s of Object.keys(byStatus)) {
        byStatus[s].sort((a, b) => a.name.localeCompare(b.name));
      }
      return STATUS_ORDER.map(status => ({ status, items: byStatus[status] || [] }));
    },

    activeWorkflow() {
      return this.activeId ? this.workflows.find(w => w.id === this.activeId) || null : null;
    },

    canSave() {
      return !!this.composer.name && !!(this.studioConfig.steps && this.studioConfig.steps.length);
    },

    // Identifies the workflow under edit for the agent's context, so it can
    // say "the Readmission cohort workflow" rather than "the canvas". Null
    // for a brand-new composition that has no row yet.
    stackPct() {
      return this.activeSheet ? this.sheetCanvasPct : this.canvasPct;
    },

    activeSheet() {
      return this.sheets.find((s) => s.id === this.bottomTab) || null;
    },

    sheetSource() {
      const wf = this.activeWorkflow;
      const unchanged = wf && workflowConfigKey(wf.studio_config) === workflowConfigKey(this.studioConfig);
      if (wf && wf.status === 'approved' && wf.last_run_status === 'success' && unchanged) return { workflow_id: wf.id };
      return this.studioConfig && this.studioConfig.steps && this.studioConfig.steps.length
        ? { studio_config: this.studioConfig } : null;
    },

    dashboardWorkflowId() {
      const source = this.sheetSource;
      return source && source.workflow_id && this.sheetsSaveState !== 'Saving…' ? source.workflow_id : null;
    },

    canvasWorkflowMeta() {
      if (!this.activeId) return null;
      const wf = (this.workflows || []).find(w => w.id === this.activeId);
      return wf ? { id: wf.id, name: wf.name, status: wf.status } : null;
    },
  },

  watch: {
    'bridge.apply'(proposal) {
      if (proposal) this.applyProposal(proposal);
    },
    'bridge.saved'() {
      this.refreshWorkflows();
    },
    'bridge.sheet'(sheet) {
      if (sheet) this.addSheet(sheet);
    },
  },

  mounted() {
    this.refreshWorkflows();
    this.visibilityObserver = new ResizeObserver(([box]) => { canvasContext.visible = box.contentRect.width > 0; });
    this.visibilityObserver.observe(this.$refs.root);
    document.addEventListener('pointerdown', this.closePickerOnOutside);
  },

  beforeUnmount() {
    if (this.visibilityObserver) this.visibilityObserver.disconnect();
    canvasContext.visible = true;
    document.removeEventListener('pointerdown', this.closePickerOnOutside);
  },

  methods: {
    applyProposal(proposal) {
      if (!this.activeId && !this.composer.name && proposal.name) this.composer.name = proposal.name;
      this.studioConfig = JSON.parse(JSON.stringify(proposal.studioConfig));
      this.flashSuccess('Applied to the canvas. Press Preview to see rows, then Save.');
    },

    pick(wf) {
      this.pickerOpen = false;
      this.selection = null;
      this.selectWorkflow(wf);
    },

    pickNew() {
      this.pickerOpen = false;
      this.selection = null;
      this.startNew();
    },

    closePickerOnOutside(e) {
      if (this.pickerOpen && this.$refs.picker && !this.$refs.picker.contains(e.target)) this.pickerOpen = false;
    },

    setCanvasPct(pct) {
      const clamped = Math.min(MAX_PCT, Math.max(MIN_PCT, Math.round(pct)));
      if (this.activeSheet) this.sheetCanvasPct = clamped;
      else this.canvasPct = clamped;
      stackStore.save({ canvas: this.canvasPct, sheetCanvas: this.sheetCanvasPct });
    },

    startStackResize(e) {
      if (e.button !== 0) return;
      e.preventDefault();
      const box = this.$refs.stack.getBoundingClientRect();
      const key = this.activeSheet ? 'sheetCanvasPct' : 'canvasPct';
      const move = (ev) => { this[key] = Math.min(MAX_PCT, Math.max(MIN_PCT, ((ev.clientY - box.top) / box.height) * 100)); };
      const stop = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', stop);
        window.removeEventListener('pointercancel', stop);
        this.setCanvasPct(this[key]);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', stop);
      window.addEventListener('pointercancel', stop);
    },

    // --- rail helpers ---
    statusLabel(status) {
      return {
        draft: 'Draft',
        pending_review: 'Pending Review',
        approved: 'Approved'
      }[status] || status;
    },

    statusBadgeClass(status) {
      const base = 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ';
      const variant = {
        draft: 'bg-gray-100 text-gray-700 border-gray-300',
        pending_review: 'bg-amber-100 text-amber-800 border-amber-300',
        approved: 'bg-emerald-100 text-emerald-800 border-emerald-300',
      }[status] || 'bg-gray-100 text-gray-700 border-gray-300';
      return base + variant;
    },

    statusDotClass(status) {
      const base = 'h-1.5 w-1.5 rounded-full ';
      return base + ({
        draft: 'bg-gray-500',
        pending_review: 'bg-amber-500',
        approved: 'bg-emerald-600',
      }[status] || 'bg-gray-500');
    },

    healthDotClass(runStatus) {
      const base = 'inline-block h-2 w-2 rounded-full shrink-0 ';
      if (runStatus === 'success') return base + 'bg-green-dark animate-pulse-soft';
      if (runStatus === 'failed') return base + 'bg-red-dark';
      return base + 'bg-gray-300';
    },

    flashSuccess(message) {
      this.successMessage = message;
      setTimeout(() => { this.successMessage = null; }, 3000);
    },

    onConnectionCreated(conn) {
      this.flashSuccess(`Data connection “${conn.name}” added.`);
    },

    onAddedToDashboard(page) {
      this.flashSuccess(`Added to “${page.title}”. Open it from Insights → Dashboards.`);
    },

    flashError(message) {
      this.errorMessage = message;
      setTimeout(() => { this.errorMessage = null; }, 6000);
    },

    async refreshWorkflows() {
      this.isLoading = true;
      this.errorMessage = null;
      try {
        const { data } = await axios.get(API_BASE);
        // Tolerate a paginated response: the rail reads workflows.length and
        // iterates groupedWorkflows, both of which break on a bare object.
        this.workflows = Array.isArray(data) ? data : (data.results || []);
      } catch (error) {
        this.flashError(error.response?.data?.error || 'Failed to load workflows');
        console.error('Error loading workflows:', error);
      } finally {
        this.isLoading = false;
      }
    },

    // --- composer: load / new / save ---
    startNew() {
      this.activeId = null;
      this.composer = { name: '', expected_cadence: 'weekly', post_process_order: 0 };
      this.studioConfig = { steps: [], primary_key_columns: [] };
      this.docs = { description: '', column_docs: {} };
      this.sheets = [];
      this.bottomTab = 'data';
      this.composerError = null;
    },

    // Load a saved workflow onto the canvas. The canvas rehydrates its node
    // graph from studio_config (watches modelValue).
    selectWorkflow(wf) {
      this.activeId = wf.id;
      this.composer = {
        name: wf.name || '',
        expected_cadence: wf.expected_cadence || 'weekly',
        post_process_order: wf.post_process_order ?? 0,
      };
      this.studioConfig = wf.studio_config
        ? JSON.parse(JSON.stringify(wf.studio_config))
        : { steps: [], primary_key_columns: [] };
      this.docs = { description: wf.description || '', column_docs: { ...(wf.column_docs || {}) } };
      this.sheets = JSON.parse(JSON.stringify(wf.sheets || []));
      this.bottomTab = 'data';
      this.composerError = null;
    },

    addSheet(from = null) {
      const sheet = {
        id: `s${Date.now().toString(36)}`,
        name: (from && from.name) || `Sheet ${this.sheets.length + 1}`,
        spec: (from && from.spec) || { type: 'bar', measure: { agg: 'count' } },
      };
      this.sheets = [...this.sheets, sheet];
      this.bottomTab = sheet.id;
      this.queueSheetsSave();
    },

    updateSheet(sheet) {
      this.sheets = this.sheets.map((s) => (s.id === sheet.id ? sheet : s));
      this.queueSheetsSave();
    },

    removeSheet(id) {
      this.sheets = this.sheets.filter((s) => s.id !== id);
      if (this.bottomTab === id) this.bottomTab = 'data';
      this.queueSheetsSave();
    },

    queueSheetsSave() {
      if (!this.activeId) return;
      clearTimeout(this.sheetsTimer);
      this.sheetsSaveState = 'Saving…';
      const workflowId = this.activeId;
      this.sheetsTimer = setTimeout(async () => {
        try {
          await axios.patch(`${API_BASE}${workflowId}/`, { sheets: this.sheets });
          const wf = this.workflows.find((w) => w.id === workflowId);
          if (wf) wf.sheets = JSON.parse(JSON.stringify(this.sheets));
          this.sheetsSaveState = 'Sheets saved';
        } catch (error) {
          this.sheetsSaveState = 'Sheets not saved';
          this.flashError(error.response?.data?.sheets?.[0] || 'Could not save the sheets.');
        }
      }, 1000);
    },

    onAddSource(da) {
      this.$refs.canvas.addDaByClick(da);
    },

    onFieldClick(field) {
      if (this.activeSheet && this.$refs.sheet && !field.forceProfile) {
        this.$refs.sheet.addField(field);
        return;
      }
      this.bottomTab = 'data';
      this.$nextTick(() => this.$refs.dataPane.focusColumn(field.name));
    },

    onFieldAction({ action, field }) {
      if (action === 'calc') {
        const fn = { date: 'year', number: 'round', text: 'trim' }[field.type] || 'case';
        this.$refs.canvas.openCalcEditor({ fn, column: field.name, name: `${field.name}_${fn.toUpperCase()}` });
      } else if (action === 'filter') {
        this.$refs.canvas.openFilterFor(field.name);
      } else {
        this.onFieldClick({ ...field, forceProfile: true });
      }
    },

    toggleDocs() {
      this.docsOpen = !this.docsOpen;
      if (!this.docsOpen) return;
      const canvas = this.$refs.canvas;
      const live = canvas ? [...canvas.calcColumns, ...canvas.calculated.map((c) => c.name)] : [];
      this.docColumns = [...new Set([...live, ...Object.keys(this.docs.column_docs)])];
    },

    setColumnDoc(column, text) {
      const next = { ...this.docs.column_docs };
      if (text.trim()) next[column] = text;
      else delete next[column];
      this.docs.column_docs = next;
    },

    async saveDocs() {
      this.savingDocs = true;
      try {
        await axios.patch(`${API_BASE}${this.activeId}/`, {
          description: this.docs.description, column_docs: this.docs.column_docs,
        });
        this.flashSuccess('Documentation saved.');
        await this.refreshWorkflows();
      } catch (error) {
        const data = error.response?.data;
        this.flashError(data?.error || (data && typeof data === 'object' ? Object.values(data).flat()[0] : null) || 'Could not save the documentation.');
      } finally {
        this.savingDocs = false;
      }
    },

    async saveWorkflow() {
      if (!this.studioConfig.steps || !this.studioConfig.steps.length) {
        this.composerError = 'Add at least one data source on the canvas.';
        return;
      }
      this.composerError = null;
      this.saving = true;
      const payload = {
        name: this.composer.name,
        expected_cadence: this.composer.expected_cadence,
        post_process_order: this.composer.post_process_order || 0,
        studio_config: this.studioConfig,
      };
      if (!this.activeId) {
        payload.description = this.docs.description;
        payload.column_docs = this.docs.column_docs;
        payload.sheets = this.sheets;
      }
      try {
        if (this.activeId) {
          const { data } = await axios.patch(`${API_BASE}${this.activeId}/`, payload);
          this.flashSuccess('Workflow updated.');
          await this.refreshWorkflows();
          if (data && data.id) this.activeId = data.id;
        } else {
          const { data } = await axios.post(API_BASE, payload);
          this.flashSuccess('Workflow created.');
          await this.refreshWorkflows();
          // Keep the new workflow loaded so the user can immediately Submit.
          if (data && data.id) this.selectWorkflow(this.workflows.find(w => w.id === data.id) || data);
        }
      } catch (error) {
        const data = error.response?.data;
        this.flashError(
          data?.error ||
          (data && typeof data === 'object' ? Object.values(data).flat()[0] : null) ||
          'Failed to save workflow'
        );
      } finally {
        this.saving = false;
      }
    },

    // --- lifecycle actions ---
    // Submit is non-destructive → fire directly.
    submitForReview(wf) {
      return this._action(wf, 'submit-for-review', 'Submitted for review.');
    },

    askApprove(wf) {
      this._openConfirm({
        title: 'Approve workflow?',
        body: wf.expected_cadence === 'none'
          ? `Approving “${wf.name}” freezes its SQL. It is not scheduled; materialize it by hand when you want an output table.`
          : `Approving “${wf.name}” freezes its SQL and schedules it to materialize on its cadence.`,
        confirmLabel: 'Approve',
        confirmClass: 'btn-accent',
        onConfirm: () => this._action(wf, 'approve', 'Workflow approved.'),
      });
    },
    materializeNow(wf) {
      return this._action(wf, 'materialize', 'Materializing in the background.');
    },
    askReject(wf) {
      this._openConfirm({
        title: 'Reject workflow?',
        body: `“${wf.name}” will be sent back to draft for edits.`,
        confirmLabel: 'Reject',
        confirmClass: 'btn-secondary',
        onConfirm: () => this._action(wf, 'reject', 'Workflow sent back to draft.'),
      });
    },
    confirmDelete(wf) {
      this._openConfirm({
        title: 'Delete workflow?',
        body: `This permanently deletes “${wf.name}”. This cannot be undone.`,
        confirmLabel: 'Delete',
        confirmClass: 'btn-danger',
        onConfirm: () => this._deleteWorkflow(wf),
      });
    },

    _openConfirm({ title, body, confirmLabel, confirmClass, onConfirm }) {
      this.confirm = { visible: true, busy: false, title, body, confirmLabel, confirmClass, onConfirm };
    },
    closeConfirm() {
      this.confirm.visible = false;
    },

    async _action(wf, verb, successText) {
      this.confirm.busy = true;
      this.actingId = wf.id;
      try {
        await axios.post(`${API_BASE}${wf.id}/${verb}/`);
        this.closeConfirm();
        this.flashSuccess(successText);
        await this.refreshWorkflows();
        // Keep the composer in sync if the acted-on workflow is loaded.
        if (this.activeId === wf.id) {
          const updated = this.workflows.find(w => w.id === wf.id);
          if (updated) this.selectWorkflow(updated);
        }
      } catch (error) {
        this.closeConfirm();
        this.flashError(error.response?.data?.error || `Failed to ${verb.replace(/-/g, ' ')}`);
      } finally {
        this.confirm.busy = false;
        this.actingId = null;
      }
    },

    async _deleteWorkflow(wf) {
      this.confirm.busy = true;
      this.actingId = wf.id;
      try {
        await axios.delete(`${API_BASE}${wf.id}/`);
        this.closeConfirm();
        this.flashSuccess('Workflow deleted.');
        // If the deleted workflow was loaded, clear the composer.
        if (this.activeId === wf.id) this.startNew();
        await this.refreshWorkflows();
      } catch (error) {
        this.closeConfirm();
        this.flashError(error.response?.data?.error || 'Failed to delete workflow');
      } finally {
        this.confirm.busy = false;
        this.actingId = null;
      }
    },
  }
};
</script>

<style scoped>
/* Elevated motion layer — additive polish, no design-system tokens changed. */

/* Skeleton shimmer for the loading state. */
.skeleton-shimmer {
  background: linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 37%, #f3f4f6 63%);
  background-size: 400% 100%;
  animation: shimmer 1.4s ease infinite;
}
@keyframes shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

/* Softer, slower pulse than Tailwind's default for the success health dot. */
.animate-pulse-soft {
  animation: pulse-soft 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
@keyframes pulse-soft {
  0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(56, 154, 108, 0.5); }
  50% { opacity: 0.85; box-shadow: 0 0 0 4px rgba(56, 154, 108, 0); }
}

.wf-btabs { display: flex; align-items: flex-end; gap: 4px; padding: 0 16px; border-bottom: 1px solid #E8E9E9; flex-shrink: 0; background: #FAFAFA; }
.wf-btab { display: inline-flex; align-items: center; gap: 8px; padding: 8px 16px; border: 0; border-bottom: 2px solid transparent; background: transparent; font-size: 13px; color: #626264; cursor: pointer; }
.wf-btab:hover { color: #353537; }
.wf-btab.is-on { color: #1E1F21; font-weight: 600; border-bottom-color: #F76734; }
.wf-btab:focus-visible { outline: 2px solid #4573D2; outline-offset: -2px; }
.wf-btab-x { color: #8E8F90; font-size: 14px; line-height: 1; }
.wf-btab-x:hover { color: #D53D3D; }
.wf-btab-add { font-size: 16px; color: #4C6E97; padding: 8px 12px; }
.wf-btab-state { margin-left: auto; padding-bottom: 8px; font-size: 11px; color: #626264; }
.wf-hsplit { flex-shrink: 0; height: 8px; margin: 0 16px; cursor: row-resize; touch-action: none; position: relative; }
.wf-hsplit::before { content: ''; position: absolute; left: 0; right: 0; top: 3px; height: 2px; border-radius: 1px; background: #e5e7eb; transition: background .15s; }
.wf-hsplit:hover::before { background: #cfcabf; }

/* The canvas fills the composer pane (overrides its fixed 440px height). */
.wf-canvas-fill { height: 100%; }
.wf-canvas-fill :deep(.wf-canvas-wrap) { height: 100%; }

/* Toast: slide up + fade. */
.wf-toast-enter-active, .wf-toast-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.wf-toast-enter-from, .wf-toast-leave-to { opacity: 0; transform: translateY(12px); }

/* Confirm dialog: backdrop fade + panel scale-in. */
.wf-fade-enter-active, .wf-fade-leave-active { transition: opacity 0.16s ease; }
.wf-fade-enter-from, .wf-fade-leave-to { opacity: 0; }
.wf-fade-enter-active .wf-fade-panel { transition: transform 0.18s cubic-bezier(0.16, 1, 0.3, 1); }
.wf-fade-enter-from .wf-fade-panel { transform: translateY(6px) scale(0.97); }

/* Respect reduced-motion preferences. */
@media (prefers-reduced-motion: reduce) {
  .skeleton-shimmer, .animate-pulse-soft,
  .wf-toast-enter-active, .wf-toast-leave-active,
  .wf-fade-enter-active, .wf-fade-leave-active,
  .wf-fade-enter-active .wf-fade-panel { animation: none; transition: none; }
}
</style>
